import { AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { GridComponent, RowDeselectEventArgs, RowSelectEventArgs, SortService, FilterService, SearchService } from '@syncfusion/ej2-angular-grids';
import { catchError } from 'rxjs/operators';
import { IApiTypedResult, Order, OrderUser, IDashboardRequest, IPaginatedResult, createDefaultDashboardRequest } from '../../shared/models/index';
import { OrderService, StorageManagerService, AuthorizationService, SettingsService } from '../../shared/services/index';
import { Utilities } from '../../shared/services/utilities';
import { Subscription } from 'rxjs';
import { IApiResult } from '../../shared/models/api-result';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { TooltipComponent, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { DeleteButtonComponent } from '../common/delete-button/delete-button.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { EmitType } from '@syncfusion/ej2-base';
import { fadeInOut } from '../../shared/helpers/animations';
import { GridPreferencesService } from '../../shared/services/grid-preferences.service';

@Component({
  selector: 'order-tracker',
  templateUrl: './order-tracker.component.html',
  styleUrls: ['./order-tracker.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  animations: [fadeInOut],
  providers: [SortService, FilterService, SearchService],
  standalone: false
})
export class OrderTrackerComponent implements OnInit, AfterViewInit, OnDestroy {

  showWelcomeMessage: boolean;
  sub: Subscription;


  // Angular form that contains controls
  orderTrackerForm: UntypedFormGroup;

  @ViewChild('ngbNav', { static: true })
  public tabset: NgbNav;

  @ViewChild('orderTrackerAllGrid')
  public orderTrackerAllGrid: GridComponent;

  @Input() orderTrackerAllGridData: any;

  @ViewChild('orderTrackerDraftGrid')
  public orderTrackerDraftGrid: GridComponent;

  @ViewChild('deleteOrderDraftButton')
  public deleteButtonDrafts: DeleteButtonComponent;

  @ViewChildren(TooltipComponent) toolTips: QueryList<TooltipComponent>;
  public tooltipObj: TooltipComponent;

  @Input() orderTrackerDraftGridData: any = { result: [], count: 0 };

  // Separate page settings for each grid
  public allOrdersPageSettings: Object;
  public draftsPageSettings: Object;

  // Server-side pagination state
  allOrdersRequest: IDashboardRequest;
  draftsRequest: IDashboardRequest;

  // Loading states
  loadingAllOrders = true;
  loadingDrafts = false;
  private draftsLoadTimer: any;
  private suppressDraftStateChanges = false;
  private draftStateSuppressionTimer: any;
  private hasActivatedDraftTab = false;

  // Skip flags to prevent dataStateChange loops after programmatic data updates

  // Selection persistence
  private selectedOrderRevisionIds = {
    all: null as number | null,
    drafts: null as number | null
  };

  private readonly dateFilterKeys: { [key: string]: { from: string; to: string } } = {
    createdOn: { from: 'createdOnFrom', to: 'createdOnTo' },
    modifiedOn: { from: 'modifiedFrom', to: 'modifiedTo' },
    confirmedDueDate: { from: 'confirmedDueDateFrom', to: 'confirmedDueDateTo' },
    actualDeliveryDate: { from: 'actualDeliveryDateFrom', to: 'actualDeliveryDateTo' }
  };

  // Modal properties
  @ViewChild('orderTrackerModal')
  ejDialogOrderTracker: DialogComponent;

  position: object = { X: 'center', Y: 'center' };
  modalWidth = '32%';
  modalHeight = 'auto';
  animationSettings: Object = { effect: 'Zoom' };
  isModal = true;
  orderTrackerDialogVisible: boolean;

  // Modal template variables
  currentOrderStatus: string = '';
  currentSelectedToolbarAction = {
    isAcceptDelivery: false,
    isResubmitOrder: false,
    isRequestOrderCancellation: false
  }

  // Common to both grids
  public orderTrackerGridInitialPage: Object;
  public orderTrackerGridFormatOptions: object;
  public orderTrackerGridFormatOptionsDate: object;
  public orderTrackerGridToolbarOptions: Object;

  // What actions are allowed for which statuses
  public whichStatusesAllowAcceptDelivery: string[];
  public whichStatusesAllowResubmit: string[];
  public whichStatusesAllowCancel: string[];
  public whichStatusesAllowDelete: string[];

  // Controls button enabled states for the two grids
  canAcceptDelivery = {
    all: false,
    draft: false,
    isAllowedOnGrid: true,
    isAllowedOnDraftGrid: false
  };
  canResubmit = {
    all: false,
    draft: false,
    isAllowedOnGrid: true,
    isAllowedOnDraftGrid: false
  };
  canCancel = {
    all: false,
    draft: false,
    isAllowedOnGrid: true,
    isAllowedOnDraftGrid: false
  };
  canDelete = {
    all: false,
    draft: false,
    isAllowedOnGrid: false,
    isAllowedOnDraftGrid: true
  };

  // Can user create new orders
  canCreate: boolean;
  canDeleteDraft: boolean;

  canOrderAdmin: boolean;
  canOrderResubmit: boolean;
  canCancelOrder: boolean;

  selectedOrderRevisionId = {
    all: null as number,
    draft: null as number
  };

  pageState: string;
  errorMessage: string;
  activeTab: string;
  public clipmodecolumn = { class: 'e-tooltipcolumn' };

  // Messages
  ORDER_RESUBMIT_MESSAGE = `You have elected to resubmit this order for additional processing.&nbsp;We will copy the contents of this order and create a new one so you can update and submit to Reed Tech for processing.&nbsp;We will notify you when your order has been saved to the Drafts tab.`;
  ordersInProcess: any = {
    resubmit: [],
    acceptDelivery: [],
    cancel: []
  };

  disabledColumns: string[] = ['Order ID', 'Order Name', 'Order Status'];

  // STATIC guard against duplicate data loading during auth flow
  // Static so it persists across component recreations during auth
  private static lastLoadTimestamp: number = 0;
  private static lastLoadedCustomerId: number = null;

  constructor(private fb: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    public orderService: OrderService,
    private router: Router, private toastr: ToastrService,
    private storageManagerService: StorageManagerService,
    private authorizationService: AuthorizationService,
    public utilService: Utilities,
    private sanitizer: DomSanitizer,
    private settingsService: SettingsService,
    public gridPreferencesService: GridPreferencesService,
  ) { }


  ngOnInit() {
    // listen for the status change to determine if we need to show the welcome message

    if (!this.storageManagerService.read('WelcomeShown')) {
      this.showWelcomeMessage = true;
      this.storageManagerService.write('WelcomeShown', true);
    }
    else {
      this.showWelcomeMessage = false;
    }
    this.loadInitialToolbar();
    this.orderTrackerGridInitialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    // Initialize separate page settings for each grid
    this.allOrdersPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.draftsPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
    this.orderTrackerGridFormatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() };
    this.orderTrackerGridFormatOptionsDate = { type: 'date', format: 'M/d/yyyy' };

    // Initialize server-side pagination requests
    this.allOrdersRequest = createDefaultDashboardRequest(false);
    this.allOrdersRequest.pageSize = 25;
    this.allOrdersRequest.sortColumn = 'createdOn';
    this.allOrdersRequest.sortDirection = 'DESC';
    this.draftsRequest = createDefaultDashboardRequest(true);
    this.draftsRequest.pageSize = 25;
    this.draftsRequest.sortColumn = 'createdOn';
    this.draftsRequest.sortDirection = 'DESC';

    // Say which statuses are required for which actions
    this.whichStatusesAllowAcceptDelivery = [
      this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
    ];
    this.whichStatusesAllowResubmit = [
      this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
    ];
    this.whichStatusesAllowCancel = [
      this.orderService.ORDER_STATUS_IN_PROCESS,
      this.orderService.ORDER_STATUS_PENDING
    ];
    this.whichStatusesAllowDelete = [
      this.orderService.ORDER_STATUS_DRAFT
    ];

    // Tell angular form what fields it has.
    this.orderTrackerForm = this.fb.group({
      orderTrackerAllGrid: [''],
      orderTrackerDraftGrid: ['']
    });

    // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex()

    this.activeTab = 'AllOrders';

    // Trigger data loading for both grids (guarded against duplicate calls)
    this.triggerDataLoad();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  hideDialog: EmitType<object> = () => {
    this.ejDialogOrderTracker.hide();
  }

  orderTrackerDialogButtons: Object[] = [{
    click: this.hideDialog.bind(this),
    buttonModel: {
      content: 'OK',
      isPrimary: true
    }
  }]

  loadInitialToolbar() {
    this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

    this.canOrderResubmit = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN)
      || this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);

    this.canCancelOrder = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER);

    // Allowed to make new orders?
    this.canCreate = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);

    this.canDeleteDraft = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_DELETE_DRAFT_ORDER);

    this.canResubmit.isAllowedOnGrid = this.canOrderResubmit;
    this.canAcceptDelivery.isAllowedOnGrid = this.canOrderResubmit;
    this.canCancel.isAllowedOnGrid = this.canCancelOrder;

    this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.all,
      this.canDelete.isAllowedOnGrid,
      this.canAcceptDelivery.isAllowedOnGrid,
      this.canResubmit.isAllowedOnGrid,
      this.canCancel.isAllowedOnGrid,
      this.canCreate);
  }

  tabChanged(event) {
    this.activeTab = event.nextId;

    this.currentOrderStatus = '';
    this.resetCurrentSelectedToolbarAction();

    if (this.activeTab === 'DraftOrders') {
      this.canDelete['draft'] = (this.whichStatusesAllowDelete as any).includes('Draft');
      this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.draft && this.canDeleteDraft,
        this.canDelete.isAllowedOnDraftGrid,
        this.canAcceptDelivery.isAllowedOnDraftGrid,
        this.canResubmit.isAllowedOnDraftGrid,
        this.canCancel.isAllowedOnDraftGrid,
        this.canCreate);

      if (!this.hasActivatedDraftTab) {
        this.hasActivatedDraftTab = true;
        this.suppressDraftStateChanges = true;
        this.draftStateSuppressionTimer = setTimeout(() => {
          this.suppressDraftStateChanges = false;
          this.draftStateSuppressionTimer = null;
        }, 500);
      }
    }
    else {
      this.orderTrackerGridToolbarOptions = this.createCustomToolbarItems(this.canCancel && this.canDelete.all,
        this.canDelete.isAllowedOnGrid,
        this.canAcceptDelivery.isAllowedOnGrid,
        this.canResubmit.isAllowedOnGrid,
        this.canCancel.isAllowedOnGrid,
        this.canCreate);
    }

  }

  createCustomToolbarItems(deletePermission: boolean, isDeleteAllowedOnTab: boolean, isAcceptDeliveryAllowedOnTab: boolean,
    isResubmitOrderAllowedOnTab: boolean, isRequestOrderCancellationAllowedOnTab: boolean, isCreateNewOrderAllowed: boolean) {
    let toolbarItems = [];

    if (isCreateNewOrderAllowed) {
      toolbarItems.push(
        {
          text: 'Create New Order',
          tooltipText: 'Add a new order form to this list',
          prefixIcon: 'fas fa-plus',
          id: 'create-form-btn'
        }
      );
    }

    if (isAcceptDeliveryAllowedOnTab) {
      toolbarItems.push(
        {
          text: 'Accept Delivery',
          tooltipText: 'Accept delivery of the selected order',
          prefixIcon: 'fas fa-check',
          id: 'tool-bar-accept-delivery-btn'
        }
      );
    }

    if (isResubmitOrderAllowedOnTab) {
      toolbarItems.push(
        {
          text: 'Resubmit Order',
          tooltipText: 'Resubmit the selected order',
          prefixIcon: 'fas fa-rotate-right',
          id: 'tool-bar-resubmit-order'
        }
      );
    }

    if (isRequestOrderCancellationAllowedOnTab) {
      toolbarItems.push(
        {
          text: 'Request Order Cancellation',
          tooltipText: 'Request cancellation of the selected order',
          prefixIcon: 'fas fa-xmark',
          id: 'tool-bar-request-order-cancellation'
        }
      );
    }

    if (deletePermission && isDeleteAllowedOnTab) {
      toolbarItems.push(
        {
          text: 'Delete Order',
          tooltipText: 'Delete the selected draft order',
          prefixIcon: 'fas fa-trash',
          id: 'tool-bar-delete'
        }
      )
    }

    toolbarItems.push('Search');
    toolbarItems.push('ColumnChooser');

    return toolbarItems;
  }

  toolbarClick(args: ClickEventArgs): void {

    this.resetCurrentSelectedToolbarAction();

    if (args.item.id === 'create-form-btn' && this.canCreate) {
      this.router.navigate(["/order"]);
    }

    // Order Draft Grid Buttons
    if (this.orderTrackerDraftGrid && this.canDeleteDraft) {
      if (args.item.id === 'tool-bar-delete' && this.selectedOrderRevisionId.draft) {
        this.deleteButtonDrafts.revisionId = this.selectedOrderRevisionId.draft.toString();
        this.deleteButtonDrafts.openDeleteModal();
      }
    }
    // Order Grid Buttons
    else {
      if (args.item.id === 'tool-bar-accept-delivery-btn') {
        this.currentSelectedToolbarAction.isAcceptDelivery = true;
        if (!this.whichStatusesAllowAcceptDelivery.includes(this.currentOrderStatus)) {
          this.ejDialogOrderTracker.show();
          return;
        }
        this.acceptDelivery('all');
      }
      else if (args.item.id === 'tool-bar-resubmit-order') {
        this.currentSelectedToolbarAction.isResubmitOrder = true;
        if (!this.whichStatusesAllowResubmit.includes(this.currentOrderStatus)) {
          this.ejDialogOrderTracker.show();
          return;
        }
        this.resubmit('all');
      }
      else if (args.item.id === 'tool-bar-request-order-cancellation') {
        this.currentSelectedToolbarAction.isRequestOrderCancellation = true;
        if (!this.whichStatusesAllowCancel.includes(this.currentOrderStatus)) {
          this.ejDialogOrderTracker.show();
          return;
        }
        this.cancel('all');
      }
    }
  }

  tooltipValue(args) {

    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      switch (this.activeTab) {
        case 'AllOrders':
          grid = this.orderTrackerAllGrid;
          break;
        case 'DraftOrders':
          grid = this.orderTrackerDraftGrid;
          break;
      }

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltipObj.content = rowData[(rowInfo.column as any).field];
    }
  }

  ngAfterViewInit() {
    this.sub = this.toolTips.changes.subscribe((comps: QueryList<TooltipComponent>) => {
      this.tooltipObj = comps?.first;
    });
  }

  loadAllOrders() {
    if (!this.storageManagerService.read('currentCustomer')) return;

    this.loadingAllOrders = true;
    this.pageState = this.orderService.PAGE_STATE_LOADING;

    // Pass the full request object (pageNumber, pageSize, sort, search, filters)
    this.orderService.getOrderTrackerDataPaginated(this.allOrdersRequest).subscribe(
      (response: any) => {
        this.loadingAllOrders = false;
        this.pageState = this.orderService.PAGE_STATE_READY;

        // Unwrap paginated result — same pattern as LCRDashboard
        const result = response?.data;
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        // Transform dates on the current page only
        const transformedData = data.map(order => ({
          ...order,
          confirmedDueDate: Utilities.extractDateOnly(order.confirmedDueDate),
          actualDeliveryDate: Utilities.extractDateOnly(order.actualDeliveryDate)
        }));

        // Syncfusion server-side format: { result, count }
        // Use Angular binding (not direct mutation) — direct mutation fires dataStateChange
        // multiple times in some Syncfusion builds, causing a page-state race condition.
        this.orderTrackerAllGridData = { result: transformedData, count: totalRecords };
      },
      error => {
        this.loadingAllOrders = false;
        this.pageState = this.orderService.PAGE_STATE_READY;
        this.orderService.handleOrderError(this, error, 'Failed to load order tracker data', false);
      }
    );
  }

  loadDrafts() {
    if (!this.storageManagerService.read('currentCustomer')) return;
    if (this.loadingDrafts) return;

    this.loadingDrafts = true;
    this.draftsRequest.sortDirection = this.normalizeSortDirection(this.draftsRequest.sortDirection, 'DESC');

    // draftsRequest was initialized with createDefaultDashboardRequest(true)
    // the "true" flag tells the API to return drafts only — no client-side filter needed
    this.orderService.getOrderTrackerDataPaginated(this.draftsRequest).subscribe(
      (response: any) => {
        this.loadingDrafts = false;

        const result = response?.data;
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        const transformedData = data.map(order => ({
          ...order,
          confirmedDueDate: Utilities.extractDateOnly(order.confirmedDueDate),
          actualDeliveryDate: Utilities.extractDateOnly(order.actualDeliveryDate)
        }));

        this.orderTrackerDraftGridData = { result: transformedData, count: totalRecords };
      },
      error => {
        this.loadingDrafts = false;
        this.orderService.handleOrderError(this, error, 'Failed to load drafts', false);
      }
    );
  }

  private queueDraftsLoad() {
    if (this.draftsLoadTimer) {
      clearTimeout(this.draftsLoadTimer);
    }

    this.draftsLoadTimer = setTimeout(() => {
      this.draftsLoadTimer = null;
      this.loadDrafts();
    }, 100);
  }

  // Triggers data loading with guard against duplicate API calls.
  // Uses STATIC state to prevent redundant loads even across component recreations.
  // Allows reload when: customer changes OR 2+ seconds since last load (for intentional refreshes).
  triggerDataLoad(forceReload: boolean = false) {
    const currentCustomer = this.storageManagerService.read('currentCustomer');
    if (!currentCustomer) return;

    const customerId = typeof currentCustomer === 'object'
      ? currentCustomer.customerId
      : JSON.parse(currentCustomer).customerId;
    const now = Date.now();

    // Check if customer changed
    const customerChanged = OrderTrackerComponent.lastLoadedCustomerId !== null &&
      OrderTrackerComponent.lastLoadedCustomerId !== customerId;

    // Allow reload after 2 seconds (for intentional user actions like explicit refresh)
    const enoughTimePassed = (now - OrderTrackerComponent.lastLoadTimestamp) > 2000;

    // Skip if recently loaded for same customer (unless forced)
    if (!customerChanged && !enoughTimePassed && !forceReload && OrderTrackerComponent.lastLoadTimestamp > 0) {
      return;
    }

    // Update static tracking
    OrderTrackerComponent.lastLoadTimestamp = now;
    OrderTrackerComponent.lastLoadedCustomerId = customerId;

    // Execute the actual data loading
    this.loadAllOrders();
    console.log("loadAllOrders hit from triggerDataLoad")
    this.loadDrafts();
  }

  // Public method called by site-layout component to refresh all data
  loadData() {
    this.triggerDataLoad();
  }

  // ------------------------------------------------------------
  // ENABLED STATES
  // ------------------------------------------------------------
  setToolbarState(whichGrid: string, orderStatus: string) {
    // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
    this.canAcceptDelivery[whichGrid] = this.canOrderResubmit && (this.whichStatusesAllowAcceptDelivery as any).includes(orderStatus);
    this.canResubmit[whichGrid] = this.canOrderResubmit && (this.whichStatusesAllowResubmit as any).includes(orderStatus);
    this.canCancel[whichGrid] = this.canCancelOrder && (this.whichStatusesAllowCancel as any).includes(orderStatus);
    this.canDelete[whichGrid] = (this.whichStatusesAllowDelete as any).includes(orderStatus);
  }


  // ------------------------------------------------------------
  // GRIDS
  // ------------------------------------------------------------
  orderTrackerGridRowSelected(args: RowSelectEventArgs, whichGrid: string) {
    let selectedRowData = null;
    let orderStatus = null;
    this.selectedOrderRevisionId[whichGrid] = null;
    const selectedRecords: any[] = (whichGrid == 'all' ? this.orderTrackerAllGrid : this.orderTrackerDraftGrid).getSelectedRecords();
    if (selectedRecords) {
      selectedRowData = selectedRecords[0];
      orderStatus = selectedRowData.orderStatus;
      this.selectedOrderRevisionId[whichGrid] = selectedRowData.orderRevisionId;

      // Track for selection persistence
      if (whichGrid === 'all') {
        this.selectedOrderRevisionIds.all = selectedRowData.orderRevisionId;
      } else {
        this.selectedOrderRevisionIds.drafts = selectedRowData.orderRevisionId;
      }

      this.currentOrderStatus = orderStatus;
    }
    this.setToolbarState(whichGrid, orderStatus);
  }

  orderTrackerGridRowDeselected(args: RowDeselectEventArgs, whichGrid: string) {
    this.setToolbarState(whichGrid, null);
    this.selectedOrderRevisionId[whichGrid] = null;

    // Clear selection persistence
    if (whichGrid === 'all') {
      this.selectedOrderRevisionIds.all = null;
    } else {
      this.selectedOrderRevisionIds.drafts = null;
    }

    this.currentOrderStatus = '';
    this.resetCurrentSelectedToolbarAction();
  }

  resetCurrentSelectedToolbarAction() {
    for (let key in this.currentSelectedToolbarAction) {
      this.currentSelectedToolbarAction[key] = false;
    }
  }

  // download sourceDoc
  downloadFile(filePath: string, fileName: string) {
    this.orderService.downloadFile(filePath, fileName).subscribe(response => {
      this.orderService.postDownloadFile(response, fileName);
    },
      error => {
        this.toastr.error(error.message);
      });
  }

  // ------------------------------------------------------------
  // BUTTONS
  // ------------------------------------------------------------

  // Accept delivery of order - change status to complete.
  acceptDelivery(whichGrid: string) {
    const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
    if (this.ordersInProcess.acceptDelivery.includes(orderRevisionId)) {
      this.toastr.warning('Order is already in process.');
      return;
    }
    this.ordersInProcess.acceptDelivery.push(orderRevisionId);
    this.toastr.success('Order proccess started.');

    if (orderRevisionId && this.whichStatusesAllowAcceptDelivery.includes(this.currentOrderStatus)) {
      this.orderService.getOrderByOrderRevisionId(orderRevisionId)
        .pipe(catchError(this.orderService.handleOrderObjectError))
        .subscribe(
          apiResult => {
            const apiTypedResult = apiResult as IApiTypedResult<Order>;
            if (apiTypedResult.isSuccess) {
              // Successfully retrieved order
              const order = apiTypedResult.data;
              // Only proceed if status is valid for this operation
              // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
              if ((this.whichStatusesAllowAcceptDelivery as any).includes(order.orderStatus)) {
                // remember old values
                order.beforeOrderStatus = order.orderStatus;
                order.beforeConfirmedDate = order.confirmedDueDate;
                // set new values
                order.orderStatus = this.orderService.ORDER_STATUS_ORDER_COMPLETE;
                const modifiedByUser = this.storageManagerService.read('userData').name;
                // Save order
                this.orderService.saveOrder(order, modifiedByUser)
                  .pipe(catchError(this.orderService.handleOrderObjectError))
                  .subscribe(apiResult => {
                    // Inform user of result
                    const apiTypedResult2: IApiTypedResult<Order> = apiResult as IApiTypedResult<Order>;
                    if (apiTypedResult2.isSuccess) {
                      this.loadAllOrders();
                      console.log("loadAllOrders hit from acceptDelivery")
                      this.loadDrafts();
                      this.orderService.alert(`Order`, `Accepted delivery of order.`);
                    } else {
                      this.orderService.alert('Order', `Failed to accept delivery of order.  ${apiTypedResult2.message}`);
                    }
                    this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                  },
                    error => {
                      this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                      this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
                    });
              } else {
                this.orderService.alert('Order', `Cannot accept delivery of order in status ${order.orderStatus}`);
                this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
              }
            } else {
              this.orderService.alert('Order', `Failed to load order.  ${apiTypedResult.message}`);
              this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
            }
          },
          error => {
            this.toastr.error(error.message);
            this.ordersInProcess = this.ordersInProcess.acceptDelivery.filter(x => x !== orderRevisionId);
          }
        );
    }
  }


  // Resubmit order - clone order, set clone status to draft, add comment
  resubmit(whichGrid: string) {
    // update the order status to Order Complete and save
    const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
    if (this.ordersInProcess.resubmit.includes(orderRevisionId)) {
      this.toastr.warning('Order is already in process.');
      return;
    }
    this.ordersInProcess.resubmit.push(orderRevisionId);
    this.toastr.success('Order proccess started.');

    if (orderRevisionId && this.whichStatusesAllowResubmit.includes(this.currentOrderStatus)) {
      this.orderService.getOrderByOrderRevisionId(orderRevisionId)
        // If there's an error, force it into {isSuccess: false, message: <some message>, data: null} format
        .pipe(catchError(this.orderService.handleOrderObjectError))
        .subscribe(apiResult => {
          const apiTypedResult = apiResult as IApiTypedResult<Order>;
          if (apiTypedResult.isSuccess) {
            // Successfully retrieved order
            const order = apiTypedResult.data;
            // Only proceed if status is valid for this operation
            // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
            if ((this.whichStatusesAllowResubmit as any).includes(order.orderStatus)) {
              const modifiedByUser = this.storageManagerService.read('userData').name;
              const modifiedByUserGuid = this.storageManagerService.read('userData').userguid;
              const customerGuid = this.settingsService.getCustomerGuid();

              // Save modify user as order intitiator.
              // Need the full list of employees so we can get all the stuff
              // we need to populate OrderUser fields.
              this.orderService.getEmployees(customerGuid)
                .subscribe(allEmployeesInCompany => {
                  const initiators: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                    allEmployeesInCompany, [modifiedByUserGuid], this.orderService.ORDER_USER_TYPE_ORDER_INITIATOR);
                  order.orderUsers.push(initiators[0]);

                  // Save order
                  this.orderService.cloneOrder(order, modifiedByUser)
                    .pipe(catchError(this.orderService.handleOrderObjectError))
                    .subscribe(apiResult => {
                      // Inform user of result
                      const apiTypedResult2: IApiResult = apiResult as IApiResult;
                      if (apiTypedResult2.isSuccess) {
                        this.loadAllOrders();
                        console.log("loadAllOrders hit from resubmit")
                        this.loadDrafts();
                        this.orderService.alert(`Order`, this.ORDER_RESUBMIT_MESSAGE);
                      } else {
                        this.orderService.alert('Order', `Failed to resubmit order.  ${apiTypedResult2.message}`);
                      }
                      this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                    },
                      error => {
                        this.toastr.error(error.message);
                        this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                      });
                },
                  error => {
                    this.toastr.error(error.message);
                    this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
                  });
            } else {
              this.orderService.alert('Order', `Cannot resubmit order in status ${order.orderStatus}`);
              this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
            }
          } else {
            this.orderService.alert('Order', `Failed to resubmit order.  ${apiTypedResult.message}`);
            this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
          }
        },
          error => {
            this.toastr.error(error.message);
            this.ordersInProcess = this.ordersInProcess.resubmit.filter(x => x !== orderRevisionId);
          });
    }
  }

  // Cancel order - change status to canceled
  cancel(whichGrid: string) {
    const orderRevisionId = this.selectedOrderRevisionId[whichGrid];
    if (this.ordersInProcess.cancel.includes(orderRevisionId)) {
      this.toastr.warning('Order is already in process.');
      return;
    }
    this.ordersInProcess.cancel.push(orderRevisionId);
    this.toastr.success('Order proccess started.');

    if (orderRevisionId && this.whichStatusesAllowCancel.includes(this.currentOrderStatus)) {
      this.orderService.getOrderByOrderRevisionId(orderRevisionId)
        // If there's an error, force it into {isSuccess: false, message: <some message>, data: null} format
        .pipe(catchError(this.orderService.handleOrderObjectError))
        .subscribe(apiResult => {
          const apiTypedResult = apiResult as IApiTypedResult<Order>;
          if (apiTypedResult.isSuccess) {
            // Successfully retrieved order
            const order = apiTypedResult.data;
            // Only proceed if status is valid for this operation
            // "includes" is valid Ecmascript 2016 but VisualStudio is mad, hide it inside "any"
            if ((this.whichStatusesAllowCancel as any).includes(order.orderStatus)) {
              // remember old values
              order.beforeConfirmedDate = order.confirmedDueDate;
              const modifiedByUser = this.storageManagerService.read('userData').name;
              // Save order
              this.orderService.requestOrderCancellation(true, order, modifiedByUser, '/order-tracker', true);
            } else {
              this.orderService.alert('Order', `Cannot cancel order in status ${order.orderStatus}`);
            }
            this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
          } else {
            this.orderService.alert('Order', `Failed to load order.  ${apiTypedResult.message}`);
            this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
          }
        },
          error => {
            this.toastr.error(error.message);
            this.ordersInProcess = this.ordersInProcess.cancel.filter(x => x !== orderRevisionId);
          });
    }
  }

  // Handle actionBegin - change filter operator from "startswith" to "contains"
  public begin(args: any): void {
    if (args.requestType === 'filtering') {
      // Change operator from "startswith" to "contains" for text columns
      if (args.columns) {
        args.columns.forEach(col => {
          if (col.field !== 'createdOn' && col.field !== 'modifiedOn' &&
            col.field !== 'confirmedDueDate' && col.field !== 'actualDeliveryDate') {
            col.operator = 'contains';
          }
        });
      }
      // Don't cancel - let dataStateChange handle the actual filtering
    }
  }

  // Handle all orders grid data state changes (paging, sorting, searching, filtering)
  onAllOrdersDataStateChange(state: any): void {
    // Skip initial data load (handled by ngOnInit) - no action or refresh without search
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.allOrdersRequest, state);
    console.log("loadAllOrders hit from onAllOrdersDataStateChange")
    this.loadAllOrders();
  }

  // Handle drafts grid data state changes (paging, sorting, searching, filtering)
  onDraftsDataStateChange(state: any): void {
    if (this.suppressDraftStateChanges) {
      return;
    }

    // Skip passive refreshes. Real state changes are coalesced because Syncfusion can emit
    // paging/sorting/searching events in a burst when the lazy draft tab is first created.
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.draftsRequest, state);
    this.queueDraftsLoad();
  }

  // Update the pagination request based on Syncfusion grid state (paging, sorting, searching, filtering)
  private updateRequestFromState(request: IDashboardRequest, state: any): void {
    // Handle paging - use skip and take from dataStateChange
    if (state.skip !== undefined && state.take !== undefined) {
      request.pageNumber = Math.floor(state.skip / state.take) + 1;
      request.pageSize = state.take;
    }

    // Handle sorting
    if (state.sorted && state.sorted.length > 0) {
      const sortInfo = state.sorted[0];
      request.sortColumn = sortInfo.name;
      request.sortDirection = this.normalizeSortDirection(sortInfo.direction, 'DESC');
    } else if (state.action && state.action.requestType === 'sorting') {
      // Also check action for sorting info
      if (state.action.columnName) {
        request.sortColumn = state.action.columnName;
        request.sortDirection = this.normalizeSortDirection(state.action.direction, 'DESC');
      } else {
        request.sortColumn = '';
        request.sortDirection = 'DESC';
      }
    }

    // Handle searching from dataStateChange
    if (state.search && state.search.length > 0) {
      request.searchTerm = state.search[0].key || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (state.action && state.action.requestType === 'searching') {
      // Handle searching from action event
      request.searchTerm = state.action.searchString || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (!state.search || state.search.length === 0) {
      // Clear search term when no search is active
      if (request.searchTerm) {
        request.searchTerm = '';
        request.pageNumber = 1;
      }
    }

    // Handle filtering from dataStateChange - rebuild filters from state.where
    if (state.action && (state.action.requestType === 'filtering' || state.action.requestType === 'clearfiltering')) {
      const newFilters: { [key: string]: string } = {};

      if (state.where && state.where.length > 0) {
        state.where.forEach(condition => {
          if (condition.predicates && condition.predicates.length > 0) {
            condition.predicates.forEach(pred => {
              if (pred.field && pred.value !== undefined && pred.value !== null && pred.value !== '') {
                this.addColumnFilter(newFilters, pred.field, pred.value, pred.operator);
              }
            });
          } else if (condition.field && condition.value !== undefined && condition.value !== null && condition.value !== '') {
            this.addColumnFilter(newFilters, condition.field, condition.value, condition.operator);
          }
        });
      }

      request.columnFilters = newFilters;
      request.pageNumber = 1;
    }
  }

  private addColumnFilter(filters: { [key: string]: string }, field: string, value: any, operator?: string): void {
    if (!field || value === undefined || value === null || value === '') {
      return;
    }

    const dateKeys = this.dateFilterKeys[field];
    if (dateKeys) {
      const dateValue = this.formatFilterDate(value);
      if (!dateValue) {
        return;
      }

      const normalizedOperator = (operator || 'equal').toLowerCase();
      if (normalizedOperator.indexOf('greater') >= 0) {
        filters[dateKeys.from] = dateValue;
      } else if (normalizedOperator.indexOf('less') >= 0) {
        filters[dateKeys.to] = dateValue;
      } else {
        filters[dateKeys.from] = dateValue;
        filters[dateKeys.to] = dateValue;
      }
      return;
    }

    filters[field] = String(value);
  }

  private formatFilterDate(value: any): string {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else {
      date = new Date(value);
    }

    if (isNaN(date.getTime())) {
      return '';
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** Called by the grids after paging/sorting/filtering so we can reselect the row. */
  gridActionComplete(args: any): void {
    switch (args?.requestType) {
      case 'paging':
      case 'sorting':
      case 'filtering':
      case 'searching':
      case 'refresh':
        this.restoreSelectionForActiveTab();
        break;
    }
  }

  private restoreSelectionForActiveTab(): void {
    const grid = this.activeTab === 'DraftOrders' ? this.orderTrackerDraftGrid : this.orderTrackerAllGrid;
    const id = this.activeTab === 'DraftOrders' ? this.selectedOrderRevisionIds.drafts : this.selectedOrderRevisionIds.all;

    if (!grid || !id) {
      return;
    }

    const view = grid.getCurrentViewRecords() as any[];

    let rowIndex = -1;
    if (view && view.length) {
      for (let i = 0; i < view.length; i++) {
        if (view[i] && view[i].orderRevisionId === id) {
          rowIndex = i;
          break;
        }
      }
    }

    if (rowIndex >= 0) {
      grid.selectRow(rowIndex);
    }
  }

  private normalizeSortDirection(direction: string, defaultDirection: string = 'ASC'): string {
    if (!direction) {
      return defaultDirection;
    }

    return direction.toLowerCase().startsWith('desc') ? 'DESC' : 'ASC';
  }

  ngOnDestroy() {
    if (this.draftsLoadTimer) {
      clearTimeout(this.draftsLoadTimer);
    }

    if (this.draftStateSuppressionTimer) {
      clearTimeout(this.draftStateSuppressionTimer);
    }

    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

}
